import React from 'react'
import { FormattedMessage } from 'react-intl'

import Checkbox from 'components/Checkbox'
import Button from 'components/Button'
import Select from 'components/Select'
import TrashBox from 'assets/icons/trash-box'
import DropdownIndicatorIconOpen from 'assets/icons/dropdown-indicator-opened24px'
import DropdownIndicatorIconClosed from 'assets/icons/dropdown-indicator-closed24px'
import { SpecieParameterToggleWrapper } from 'flow/SiteProposal/pages/Assessment/components/SpeciesDetails/SpeciesDetailsModal/style'
import {
  EcosystemRow,
  RowActions,
  DetailColumns,
  DetailSection,
  SectionTitle,
  TimingWrapper,
  TooltipBtnWrapper,
  TooltipBtnText
} from './style'
import CopyIcon from 'assets/icons/copy-icon'
import PasteIcon from 'assets/icons/paste-icon'
import {
  setCopyPayload,
  subscribe,
  getCopyPayload,
  subscribePasteUsed,
  setPasteUsed,
  getPasteUsed,
  setActiveDetail,
  getActiveDetail,
  subscribeActive,
  addActivePaste,
  clearActivePastes
} from './ecosystemCopyStore'
import { requestPressureCopyPaste } from 'api/monitoring'
import { useParams } from 'react-router-dom'
import Tooltip from 'components/Tooltip'
import { TooltipWrapper } from 'flow/SiteMonitoring/pages/Response/style'
import { TooltipHintWrapper } from 'flow/SiteProposal/pages/About/style'
import InfoIcon from 'assets/icons/info-icon'
import Modal from 'components/Modal'
import FlowRequestModal from 'components/FlowRequestModal'
import CircleIcon from 'assets/icons/circle-icon'
import ProgressComplete from 'assets/icons/progress-complete'

const EcosystemAccordion = ({
  item,
  isOpen,
  toggleAccordion,
  getChecked,
  onToggleNode,
  getDepth,
  onOpenDetails,
  onClone,
  onDelete,
  isSpeciesView,
  scopeSelections = {},
  severitySelections = {},
  timingSelections = {},
  updateTaxonGroupScope,
  updateTaxonGroupSeverity,
  updateTaxonGroupTiming,
  isScopeOptions = [],
  isSeverityOptions = [],
  isTimingOptions = [],
  monitorThreatId,
  isEditable
}) => {
  const params = useParams()
  const depth = getDepth ? getDepth(item) : 0
  const isChild = depth > 0
  // compute base checked using parent-provided `getChecked` when available
  let baseChecked = false
  if (getChecked) {
    if (Array.isArray(item.children) && item.children.length > 0) {
      try {
        baseChecked = item.children.every((c) => getChecked(c))
      } catch (e) {
        baseChecked = Boolean(getChecked(item))
      }
    } else {
      baseChecked = Boolean(getChecked(item))
    }
  } else {
    baseChecked = Boolean(item.isChecked)
  }
  const checked = baseChecked
  const [copyPayload, setCopyPayloadState] = React.useState(() => getCopyPayload() || null)
  const instanceId = React.useRef(`ec-header-${Math.random().toString(36).slice(2)}`)
  const threatTimmingId = localStorage.getItem('site_threat_timing_id')
  const fallbackThreatTimingId = threatTimmingId ? Number(threatTimmingId) : null
  const [isEcosystemDeleteModal, setIsEcosystemDeleteModal] = React.useState(false)
  const [selectedItem, setSelectedItem] = React.useState(null)

  React.useEffect(() => {
    const unsub = subscribe((p) => setCopyPayloadState(p))
    return () => unsub()
  }, [])

  // subscribe to active detail so header reflects active copy/paste state
  const [activeDetail, setActiveDetailState] = React.useState(() => getActiveDetail())
  React.useEffect(() => {
    const unsubA = subscribeActive((v) => setActiveDetailState(v))
    return () => unsubA()
  }, [])

  // copy visual is driven directly from `copyPayload` and shared active state
  React.useEffect(() => {
    const handler = (e) => {
      const d = e && e.detail ? e.detail : {}
      if (d && d.source === instanceId.current) return
      // When another COPY is selected elsewhere, clear this header's paste visual state
      if (d.button === 'copy' && ((d.scope === 'header' && d.id !== item.id) || d.scope === 'species')) {
        // other copies clear our paste visuals; store will also update subscribers
      }
    }
    document.addEventListener('ecosystem:buttonToggled', handler)

    return () => {
      document.removeEventListener('ecosystem:buttonToggled', handler)
    }
  }, [item.id])

  const handleToggle = (event) => {
    event.stopPropagation()
    toggleAccordion()
  }

  const collectSpeciesNodes = (node) => {
    const results = []
    const walk = (n) => {
      if (!n) return
      if (n.kind === 'species') {
        results.push(n)
        return
      }
      if (Array.isArray(n.children)) {
        n.children.forEach((c) => walk(c))
      }
    }
    walk(node)
    return results
  }

  // disable paste when no copy payload exists; when a copy exists only
  const isEmptyObject = copyPayload && typeof copyPayload === 'object' && Object.keys(copyPayload).length === 0
  // If no copy payload exists, disable paste. If a copy exists, only disable paste for the same taxon.
  const isPasteDisabled = copyPayload ? Boolean(isEmptyObject || copyPayload.copy_id === item?.id) : true
  const [isPasteDisabledBtn, setIsPasteDisabledBtn] = React.useState(isPasteDisabled || getPasteUsed())

  React.useEffect(() => {
    const unsub = subscribePasteUsed((v) => {
      setIsPasteDisabledBtn(Boolean(isPasteDisabled || v))
    })
    return () => unsub()
  }, [isPasteDisabled])

  // update header paste-disabled when global copy payload changes
  React.useEffect(() => {
    try {
      const selfCopied = activeDetail && activeDetail.copy && activeDetail.copy.id === item.id && activeDetail.copy.button === 'copy'
      setIsPasteDisabledBtn(Boolean(isPasteDisabled || getPasteUsed() || selfCopied))
    } catch (e) {}
  }, [isPasteDisabled, copyPayload])

  const handlePaste = (taxonName) => {
    const payload = getCopyPayload()
    if (!payload) return

    const finalPayload = {
      copy_from_ecosystem: payload.copy_id || null,
      paste_to_ecosystem: [taxonName],
      monitor_threats_id: Number(payload?.monitor_threats_id) || Number(monitorThreatId) || null,
      site_id: params.id || null,
      site_pressure_id: Number(params.pressureId) || null,
      filter_by: 2
    }
    updateTaxonGroupScope?.(item.id, payload?.scope_id || null)
    updateTaxonGroupSeverity?.(item.id, payload?.severity_id || null)
    updateTaxonGroupTiming?.(item.id, payload?.site_threat_timing_id || null)
    // remove unwanted keys from finalPayload before sending
    ;['scope_id', 'severity_id', 'site_threat_timing_id'].forEach((k) => {
      if (k in finalPayload) delete finalPayload[k]
    })
    //API call
    try {
      setPasteUsed(true)
    } catch (e) {}
    requestPressureCopyPaste(finalPayload)
      .then((response) => {
        //handle response
        console.log('EcosystemAccordionContent handlePaste response data: ', response)
      })
      .catch((error) => {
        //handle error
        console.error('EcosystemAccordionContent handlePaste error data: ', error)
      })
      .finally(() => {
        try {
          setPasteUsed(false)
        } catch (e) {}
      })
  }

  const commonTooltipProps = {
    style: { zIndex: 9999 },
    placement: 'bottom',
    trigger: ['hover'],
    align: { offset: [0, 16] },
    type: 'big'
  }

  const handleDeleteEcosystem = (item) => {
    if (selectedItem) {
      onDelete?.(selectedItem);
    }
    setIsEcosystemDeleteModal(false);
    setSelectedItem(null);
  }

  const getSelectionValue = (selections, fallback = null) => {
    if (Object.prototype.hasOwnProperty.call(selections || {}, item.id)) {
      return selections[item.id]
    }
    return fallback
  }

  const resolveSelectionId = (value) => {
    if (value === undefined || value === null) return null
    if (typeof value === 'object') return value.value ?? value.id ?? null
    return value
  }

  const hasSelection = (value) => {
    const resolvedValue = resolveSelectionId(value)
    return resolvedValue !== null && resolvedValue !== undefined && resolvedValue !== '' && Number(resolvedValue) !== 999
  }

  const checkScopeValue = getSelectionValue(scopeSelections, item?.raw?.scope_id ?? null)
  const checkSeverityValue = getSelectionValue(severitySelections, item?.raw?.severity_id ?? null)
  const checkTimingValue = getSelectionValue(timingSelections, item?.raw?.site_threat_timing_id ?? fallbackThreatTimingId)
  const isEvaluated = hasSelection(checkScopeValue) && hasSelection(checkSeverityValue) && hasSelection(checkTimingValue)

  return (
    <>
      <EcosystemRow>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Checkbox
            checked={checked}
            disabled={!isEditable}
            onChange={(event) => {
              event.stopPropagation()
              onToggleNode?.(item)
            }}
            label={
              <span className="species-label">
                {isEvaluated ? <ProgressComplete /> : <CircleIcon />}&nbsp;
                {item.name}
              </span>
            }
          />
        </div>
        <div className="type-label"></div>

        <div className="expand-cell">
          <SpecieParameterToggleWrapper isActive={isOpen} onClick={handleToggle} style={{ cursor: 'pointer' }}>
            {isOpen ? (
              <FormattedMessage id="close" defaultMessage="Close" />
            ) : (
              <FormattedMessage id="expand" defaultMessage="Expand" />
            )}
            {isOpen ? (
              <DropdownIndicatorIconOpen style={{ marginLeft: 8 }} />
            ) : (
              <DropdownIndicatorIconClosed style={{ marginLeft: 8 }} />
            )}
          </SpecieParameterToggleWrapper>
        </div>

        <RowActions>
          <TooltipBtnWrapper>
            <Button
              height="34px"
              marginTop="0"
              type={activeDetail && activeDetail.copy && activeDetail.copy.id === item.id ? 'primary' : undefined}
              isDisabled={!isEditable}
              onClick={(event) => {
                event.stopPropagation()
                // build payload for group copy
                const scopeValue = getSelectionValue(scopeSelections, item?.raw?.scope_id ?? null)
                const severityValue = getSelectionValue(severitySelections, item?.raw?.severity_id ?? null)
                const timingValue = getSelectionValue(
                  timingSelections,
                  item?.raw?.site_threat_timing_id ?? fallbackThreatTimingId
                )

                const payload = {
                  monitor_threats_id:
                    Number(item?.monitorThreatId) ||
                    item?.monitor_threats_trigger?.[0]?.monitor_threats_id ||
                    item?.monitor_threats_id ||
                    null,
                  site_id: params?.id || null,
                  scope_id: resolveSelectionId(scopeValue),
                  severity_id: resolveSelectionId(severityValue),
                  site_threat_timing_id: resolveSelectionId(timingValue),
                  copy_id: item.id,
                }
                setCopyPayload(payload)
                try {
                  setActiveDetail({ id: item.id, button: 'copy', scope: 'header' })
                } catch (e) {}
                onClone?.(item)
              }}
            >
              <PasteIcon />
            </Button>
            <TooltipBtnText>
              <FormattedMessage id="copy" defaultMessage="Copy" />
            </TooltipBtnText>
          </TooltipBtnWrapper>

          <TooltipBtnWrapper>
            <Button
              height="34px"
              marginTop="0"
              isDisabled={!isEditable}
              type={
                activeDetail && Array.isArray(activeDetail.pastes) && activeDetail.pastes.includes(item.id)
                  ? 'primary'
                  : undefined
              }
              disabled={isPasteDisabledBtn}
              onClick={(event) => {
                if (isPasteDisabledBtn) return
                event.stopPropagation()
                try {
                  addActivePaste({ id: item.id, button: 'paste', scope: 'header' })
                } catch (e) {}
                console.log('EcosystemAccordion paste button clicked, activeDetail.pastes:', item)
                try {
                  document.dispatchEvent(
                    new CustomEvent('ecosystem:pasteButtonToggled', {
                      detail: {
                        scope: 'header',
                        id: item.id,
                        taxon: item?.name,
                        button: 'paste',
                        source: instanceId.current
                      }
                    })
                  )
                } catch (e) {}
                onOpenDetails?.(item)
                handlePaste(item?.id)
                // mark paste as used globally so other paste buttons become disabled
                try {
                  setPasteUsed(true)
                } catch (e) {}
              }}
            >
              <CopyIcon />
            </Button>
            <TooltipBtnText>
              <FormattedMessage id="paste" defaultMessage="Paste" />
            </TooltipBtnText>
          </TooltipBtnWrapper>
          <TooltipBtnWrapper>
            <Button
              type="disalbeBorder"
              height="34px"
              marginTop="0"
              isDisabled={!isEditable}
              onClick={(event) => {
                event.stopPropagation()
                setSelectedItem(item);
                setIsEcosystemDeleteModal(true);
              }}
            >
              <TrashBox />
            </Button>
            <TooltipBtnText>
              <FormattedMessage id="delete" defaultMessage="Delete" />
            </TooltipBtnText>
          </TooltipBtnWrapper>
        </RowActions>
      </EcosystemRow>

      {isOpen && (
        <>
          <DetailColumns style={{ marginTop: 24, marginBottom: 24 }}>
            <DetailSection>
              <SectionTitle>
                {isSpeciesView ? (
                  <FormattedMessage
                    id="onlyForSelectedSpeciesTitle"
                    defaultMessage="onlyForSelectedSpeciesTitle"
                  />
                ) : (
                  <TooltipWrapper>
                    <FormattedMessage
                      id="onlyForSelectedEcosystemTitle"
                      defaultMessage="onlyForSelectedEcosystemTitle"
                    />
                    <Tooltip
                      {...commonTooltipProps}
                      overlay={{
                        label: 'onlyForSelectedEcosystemTitle',
                        infoTooltip: 'onlyForSelectedEcosystemTitle'
                      }}
                    >
                      <TooltipHintWrapper style={{marginLeft: 0, width: '32px', height: '32px', fontSize: '18px'}}>
                        <InfoIcon width={24} height={24} />
                      </TooltipHintWrapper>
                    </Tooltip>
                  </TooltipWrapper>
                )}
              </SectionTitle>
            </DetailSection>
          </DetailColumns>

          <DetailColumns style={{ display: 'flex', gap: 16, padding: '2px 130px' }}>
            <DetailSection style={{ flex: 1 }}>
              <SectionTitle>
                <FormattedMessage id="scopeOfThreat" defaultMessage="scopeOfThreat" />
              </SectionTitle>
              <TimingWrapper style={{ maxWidth: '900px' }}>
                {(() => {
                  const scopeValue = scopeSelections[item.id]
                  const scopeSelected =
                    scopeValue && typeof scopeValue === 'object'
                      ? scopeValue
                      : isScopeOptions.find((opt) => (opt && (opt.value ?? opt.id)) === scopeValue) || null
                  return (
                    <Select
                      placeholder="Please select"
                      value={scopeSelected}
                      disabled={!isEditable}
                      onChange={(option) => {
                        updateTaxonGroupScope?.(item.id, option)
                        const species = collectSpeciesNodes(item)
                        try {
                          setCopyPayload(null)
                          try {
                            setActiveDetail(null)
                          } catch (e) {}
                          try {
                            clearActivePastes()
                          } catch (e) {}
                          setIsPasteDisabledBtn(true)
                        } catch (e) {}
                      }}
                      options={isScopeOptions}
                    />
                  )
                })()}
              </TimingWrapper>
            </DetailSection>

            <DetailSection style={{ flex: 1 }}>
              <SectionTitle>
                <FormattedMessage id="severityOfThreat" defaultMessage="severityOfThreat" />
              </SectionTitle>
              <TimingWrapper>
                {(() => {
                  const severityValue = severitySelections[item.id]
                  const severitySelected =
                    severityValue && typeof severityValue === 'object'
                      ? severityValue
                      : isSeverityOptions.find((opt) => (opt && (opt.value ?? opt.id)) === severityValue) || null
                  return (
                    <Select
                      placeholder="Please select"
                      value={severitySelected}
                      disabled={!isEditable}
                      onChange={(option) => {
                        updateTaxonGroupSeverity?.(item.id, option)
                        const species = collectSpeciesNodes(item)
                        try {
                          setCopyPayload(null)
                          try {
                            setActiveDetail(null)
                          } catch (e) {}
                          try {
                            clearActivePastes()
                          } catch (e) {}
                          setIsPasteDisabledBtn(true)
                        } catch (e) {}
                      }}
                      options={isSeverityOptions}
                    />
                  )
                })()}
              </TimingWrapper>
            </DetailSection>
          </DetailColumns>

          <DetailSection style={{ padding: '2px 130px' }}>
            <SectionTitle>
              <FormattedMessage id="threatTiming" defaultMessage="threatTiming" />
            </SectionTitle>
            <TimingWrapper>
              {(() => {
                const timingValue = Object.prototype.hasOwnProperty.call(timingSelections, item.id)
                  ? timingSelections[item.id]
                  : item.raw?.site_threat_timing_id || Number(threatTimmingId)
                const timingSelected =
                  timingValue && typeof timingValue === 'object'
                    ? timingValue
                    : isTimingOptions.find((opt) => (opt && (opt.value ?? opt.id)) === timingValue) || null
                return (
                    <Select
                    placeholder="selectTiming"
                    value={timingSelected}
                    disabled={!isEditable}
                    onChange={(option) => {
                      updateTaxonGroupTiming?.(item.id, option)
                      const species = collectSpeciesNodes(item)
                      try {
                        setCopyPayload(null)
                        try {
                          setActiveDetail(null)
                        } catch (e) {}
                        try {
                          clearActivePastes()
                        } catch (e) {}
                        setIsPasteDisabledBtn(true)
                      } catch (e) {}
                    }}
                    options={isTimingOptions}
                  />
                )
              })()}
            </TimingWrapper>
          </DetailSection>
        </>
      )}

      <Modal
        width="496px"
        isOpen={isEcosystemDeleteModal}
        toggleModal={setIsEcosystemDeleteModal}
        modalTitleId={'taxonDeleteModalTitle'}
      >
        <FlowRequestModal
          cancelHandler={() => {
            setIsEcosystemDeleteModal(false)
          }}
          confirmHandler={handleDeleteEcosystem}
          confirmBtnText="yesDelete"
          text={<FormattedMessage id="removeEcosystemModalText" values={{ name: <b>{selectedItem?.name}</b> }} />}
          item={''}
          approve={false}
        />
      </Modal>
    </>
  )
}

export default EcosystemAccordion
